import React from 'react';
import PropTypes from 'prop-types';
import Theme from "./Theme.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const PLACE = {
  center: {
    justifyContent: 'center'
  },
  evenly: {
    justifyContent: 'space-evenly'
  },
  right: {
    justifyContent: 'flex-end'
  },
  left: {
    justifyContent: 'flex-start'
  }
};

/**
 * react class to render a tab for the tab pane
 * @property {string} tabId - id of the tab 
 * @property {string} tabName - name that will be displayed on the tab button 
 * @property {string} className - css class to be applied to this component 
 */
class Tab extends React.Component {
  static propTypes = {
    tabId: PropTypes.string.isRequired,
    // eslint-disable-line
    tabName: PropTypes.node.isRequired,
    // eslint-disable-line
    className: PropTypes.string // eslint-disable-line
  };
  static defaultProps = {
    className: null
  };
  render() {
    const {
      children
    } = this.props;
    return children || null;
  }
}

/**
 * React class for a component with as many tabs as desired. manages which tab is displayed
 * @property {string} activeTab - id of the tab that should be active when the component is created 
 * @property {string} variant - Theme variant name to be added to the component 
 * @property {string} className - css class to be applied to this component 
 * @property {string} activeId - id of the tab that will be active 
 * @property {string} defaultId - id of the tab that will be active if there isn't an activeId specified 
 * @property {any} eventData - will be passed through as the data key to the onClick function 
 * @property {function} onTabClicked - function to be executed when a Tab gets clicked 
 */
class TabPane extends React.Component {
  static contextType = Theme.Context;
  static propTypes = {
    // An array of a certain type
    // children : PropTypes.arrayOf( PropTypes.instanceOf( Tab ) ),
    activeTab: PropTypes.string,
    variant: PropTypes.string,
    className: PropTypes.string,
    activeId: PropTypes.string,
    defaultId: PropTypes.string,
    eventData: PropTypes.any,
    onTabClicked: PropTypes.func
  };
  static defaultProps = {
    variant: null,
    className: null,
    eventData: null,
    activeTab: null,
    activeId: null,
    defaultId: null,
    onTabClicked: null
  };
  constructor(props) {
    super(props);
    this.state = {
      activeTab: props && props.activeTab || 0
    };
  }

  /**
   * (Internal) sets the activeTab, then calls onTabClicked with an object with a value property and data property
   * the value property is the tabId of the tab clicked, and the data is the eventData prop
   */
  onTabClicked = tabId => {
    const {
      onTabClicked,
      eventData
    } = this.props;
    this.setState({
      activeTab: tabId
    });
    if (onTabClicked) {
      onTabClicked({
        value: tabId,
        data: eventData
      });
    }
  };
  render() {
    let {
      activeTab
    } = this.state;
    const theme = this.context;
    const {
      placement,
      className,
      tabsClassName,
      tabClassName,
      activeId,
      defaultId,
      contentClassName,
      dividerClassName,
      variant,
      children,
      style,
      tabHeader,
      tabFooter
    } = this.props;
    const tabPane = theme.classes('TabPane', 'tabPane', variant, className);
    const tabs = theme.subclasses('TabPane', 'tabs', tabsClassName);
    const tab = theme.subclasses('TabPane', 'tab', tabClassName);
    const tabLine = theme.subclasses('TabPane', 'tabLine', dividerClassName);
    const tabContent = theme.subclasses('TabPane', 'tabContent', contentClassName);
    const tabsStyle = PLACE[placement];
    const cn = [];
    if (activeId) {
      activeTab = activeId;
    } else if (activeTab == null && defaultId) {
      activeTab = defaultId;
    }
    let selected;
    React.Children.forEach(children, childNode => {
      if (childNode && childNode.props.visible !== false) {
        cn.push(childNode);
        if (selected == null || childNode.props.tabId === activeTab) {
          selected = childNode;
        }
      }
    });
    return /*#__PURE__*/_jsxs("div", {
      className: tabPane,
      style: style,
      children: [/*#__PURE__*/_jsxs("div", {
        className: tabs,
        style: tabsStyle,
        children: [tabHeader, cn.map(c => {
          const {
            tabId,
            tabName,
            disabled
          } = c.props;
          /* eslint-disable react/button-has-type */
          return /*#__PURE__*/_jsx("button", {
            type: "button",
            "data-active": selected === c,
            className: tab,
            disabled: disabled,
            onClick: () => {
              this.onTabClicked(tabId);
            },
            children: tabName
          }, tabId);
          /* eslint-enable react/button-has-type */
        }), tabFooter]
      }), /*#__PURE__*/_jsx("div", {
        className: tabLine
      }), /*#__PURE__*/_jsx("div", {
        className: tabContent,
        children: selected
      })]
    });
  }
}
TabPane.Tab = Tab;
export default TabPane;